<template>
  <main dir="rtl" class="flex-1 pb-8">
    <div class="mx-auto px-4 sm:px-6 lg:px-8">
      <!-- Header -->
      <div class="flex justify-between items-center mb-6">
        <h2 class="text-2xl font-bold text-gray-900">إدارة جداول المعلمين</h2>
        <div class="flex gap-2">
          <button 
            v-if="selectedTeacherId"
            @click="toggleViewMode"
            class="bg-gray-600 hover:bg-gray-700 text-white px-4 py-2 rounded-lg flex items-center gap-2"
          >
            <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zM14 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z"></path>
            </svg>
            {{ viewMode === 'table' ? 'عرض مجمّع' : 'عرض جدول' }}
          </button>
          <button 
            @click="openCreateModal"
            class="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg flex items-center gap-2"
          >
            <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6"></path>
            </svg>
            إضافة جدول جديد (سريع)
          </button>
        </div>
      </div>

      <!-- Teacher Selector & Filters -->
      <div class="mb-6 bg-white p-4 rounded-lg shadow">
        <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label class="block text-sm font-medium text-gray-700 mb-2">اختر المعلم</label>
            <select 
              v-model="selectedTeacherId" 
              @change="loadSchedules"
              class="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
            >
              <option value="">اختر المعلم</option>
              <option v-for="teacher in teachers" :key="teacher.id" :value="teacher.id">
                {{ teacher.fullName }}
              </option>
            </select>
          </div>
          <div v-if="selectedTeacherId && schedules.length > 0">
            <label class="block text-sm font-medium text-gray-700 mb-2">فلترة حسب الحالة</label>
            <select 
              v-model="filterActive" 
              @change="applyFilter"
              class="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
            >
              <option value="all">الكل</option>
              <option value="true">نشط فقط</option>
              <option value="false">غير نشط فقط</option>
            </select>
          </div>
        </div>
        <div v-if="selectedTeacherId && schedules.length > 0" class="mt-4 text-sm text-gray-600">
          <span class="font-medium">إجمالي الجداول:</span> {{ filteredSchedules.length }}
        </div>
      </div>

      <!-- Loading State -->
      <div v-if="loading" class="text-center py-8">
        <div class="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
        <p class="mt-2 text-gray-600">جاري تحميل الجداول...</p>
      </div>

      <!-- View Mode: Table -->
      <div v-if="viewMode === 'table' && selectedTeacherId && filteredSchedules.length > 0" class="bg-white shadow-lg rounded-lg overflow-hidden">
        <div class="overflow-x-auto">
          <table class="min-w-full">
            <thead class="bg-gray-50">
              <tr>
                <th class="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">نوع الجدول</th>
                <th class="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">الصلاة</th>
                <th class="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">وقت البداية</th>
                <th class="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">وقت النهاية</th>
                <th class="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">يوم الأسبوع</th>
                <th class="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">النطاق الزمني</th>
                <th class="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">عدد الجلسات</th>
                <th class="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">مدة الجلسة</th>
                <th class="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">بعد الأذان (دقيقة)</th>
                <th class="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">المدة الإجمالية</th>
                <th class="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">المعرفات</th>
                <th class="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">الإنشاء/التحديث</th>
                <th class="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">الحالة</th>
                <th class="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">الإجراءات</th>
              </tr>
            </thead>
            <tbody class="bg-white divide-y divide-gray-200">
              <tr v-for="schedule in filteredSchedules" :key="schedule.id" class="hover:bg-gray-50">
                <td class="px-6 py-4 whitespace-nowrap text-sm">
                  <span
                    :class="getScheduleType(schedule) === 'custom' ? 'bg-blue-100 text-blue-800' : 'bg-slate-100 text-slate-700'"
                    class="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium"
                  >
                    {{ getScheduleTypeLabel(schedule) }}
                  </span>
                </td>
                <td class="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                  {{ getPrayerNameArabic(schedule.prayerName) || 'N/A' }}
                </td>
                <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                  {{ formatTime(schedule.sessionStartTime) }}
                </td>
                <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                  {{ formatTime(schedule.sessionEndTime) }}
                </td>
                <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                  {{ schedule.dayName || 'كل الأيام' }}
                </td>
                <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                  {{ getScheduleDateRange(schedule) }}
                </td>
                <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                  {{ schedule.sessionCount }}
                </td>
                <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                  {{ schedule.sessionDurationMinutes }} دقيقة
                </td>
                <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                  {{ schedule.sessionStartAfterMinutes ?? '-' }}
                </td>
                <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                  {{ schedule.totalDurationMinutes }} دقيقة
                </td>
                <td class="px-6 py-4 text-xs text-gray-600">
                  <div>الجدول: {{ schedule.id }}</div>
                  <div>المجموعة: {{ schedule.scheduleGroupId || '-' }}</div>
                  <div>الجلسة: {{ schedule.sessionId || '-' }}</div>
                  <div>وقت الجلسة: {{ schedule.sessionTimeId || '-' }}</div>
                </td>
                <td class="px-6 py-4 text-xs text-gray-600">
                  <div>إنشاء: {{ schedule.createdDate ? formatDateTime(schedule.createdDate) : '-' }}</div>
                  <div>تحديث: {{ schedule.updatedDate ? formatDateTime(schedule.updatedDate) : '-' }}</div>
                </td>
                <td class="px-6 py-4 whitespace-nowrap">
                  <span 
                    :class="schedule.isActive ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'"
                    class="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium"
                  >
                    {{ schedule.isActive ? 'نشط' : 'غير نشط' }}
                  </span>
                  <div v-if="isScheduleExpired(schedule)" class="text-[11px] text-amber-700 mt-1">
                    انتهت المدة (مخفي للمستخدمين بالتطبيق)
                  </div>
                </td>
                <td class="px-6 py-4 whitespace-nowrap text-sm font-medium">
                  <div class="flex gap-2">
                    <!--
                    <button 
                      @click="openEditModal(schedule.id)"
                      class="text-blue-600 hover:text-blue-900 bg-blue-50 hover:bg-blue-100 px-3 py-1 rounded"
                    >
                      تعديل
                    </button>
                    -->
                    <button 
                      @click="deleteSchedule(schedule)"
                      class="text-red-600 hover:text-red-900 bg-red-50 hover:bg-red-100 px-3 py-1 rounded"
                    >
                      حذف
                    </button>
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <!-- View Mode: Grouped by Schedule Group ID -->
      <div v-if="viewMode === 'grouped' && selectedTeacherId && orderedSchedulesByGroup" class="space-y-6">
        <template v-for="(groupSchedules, groupId) in orderedSchedulesByGroup" :key="groupId">
          <div v-if="groupSchedules && groupSchedules.length > 0" class="bg-white shadow-lg rounded-lg overflow-hidden">
          <div class="bg-gray-50 px-6 py-3 border-b border-gray-200">
            <h3 class="text-lg font-semibold text-gray-900">
              <span v-if="!groupId.startsWith('no-group')">الجدول رقم {{ groupId }}</span>
              <span v-else>جداول غير مجمعة</span>
              <span class="text-sm font-normal text-gray-600">
                ({{ groupSchedules.length }} {{ groupSchedules.length === 1 ? 'يوم' : 'أيام' }})
              </span>
              <span v-if="groupSchedules.length > 0" class="text-xs text-gray-500 block mt-1">
                الأيام: {{ groupSchedules.map(s => s.dayName || 'كل الأيام').join('، ') }}
              </span>
            </h3>
          </div>
          <div class="overflow-x-auto">
            <table class="min-w-full">
              <thead class="bg-gray-50">
                <tr>
                  <th class="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">نوع الجدول</th>
                  <th class="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">الصلاة</th>
                  <th class="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">وقت البداية</th>
                  <th class="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">وقت النهاية</th>
                  <th class="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">يوم الأسبوع</th>
                  <th class="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">النطاق الزمني</th>
                  <th class="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">عدد الجلسات</th>
                  <th class="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">مدة الجلسة</th>
                  <th class="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">بعد الأذان (دقيقة)</th>
                  <th class="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">المدة الإجمالية</th>
                  <th class="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">المعرفات</th>
                  <th class="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">الإنشاء/التحديث</th>
                  <th class="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">الحالة</th>
                  <th class="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">الإجراءات</th>
                </tr>
              </thead>
              <tbody class="bg-white divide-y divide-gray-200">
                <tr v-for="schedule in groupSchedules" :key="schedule.id" class="hover:bg-gray-50">
                  <td class="px-6 py-4 whitespace-nowrap text-sm">
                    <span
                      :class="getScheduleType(schedule) === 'custom' ? 'bg-blue-100 text-blue-800' : 'bg-slate-100 text-slate-700'"
                      class="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium"
                    >
                      {{ getScheduleTypeLabel(schedule) }}
                    </span>
                  </td>
                  <td class="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                    {{ getPrayerNameArabic(schedule.prayerName) || 'N/A' }}
                  </td>
                  <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                    {{ formatTime(schedule.sessionStartTime) }}
                  </td>
                  <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                    {{ formatTime(schedule.sessionEndTime) }}
                  </td>
                  <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                    {{ schedule.dayName || 'كل الأيام' }}
                  </td>
                  <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                    {{ getScheduleDateRange(schedule) }}
                  </td>
                  <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                    {{ schedule.sessionCount }}
                  </td>
                  <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                    {{ schedule.sessionDurationMinutes }} دقيقة
                  </td>
                  <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                    {{ schedule.sessionStartAfterMinutes ?? '-' }}
                  </td>
                  <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                    {{ schedule.totalDurationMinutes }} دقيقة
                  </td>
                  <td class="px-6 py-4 text-xs text-gray-600">
                    <div>الجدول: {{ schedule.id }}</div>
                    <div>المجموعة: {{ schedule.scheduleGroupId || '-' }}</div>
                    <div>الجلسة: {{ schedule.sessionId || '-' }}</div>
                    <div>وقت الجلسة: {{ schedule.sessionTimeId || '-' }}</div>
                  </td>
                  <td class="px-6 py-4 text-xs text-gray-600">
                    <div>إنشاء: {{ schedule.createdDate ? formatDateTime(schedule.createdDate) : '-' }}</div>
                    <div>تحديث: {{ schedule.updatedDate ? formatDateTime(schedule.updatedDate) : '-' }}</div>
                  </td>
                  <td class="px-6 py-4 whitespace-nowrap">
                    <span 
                      :class="schedule.isActive ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'"
                      class="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium"
                    >
                      {{ schedule.isActive ? 'نشط' : 'غير نشط' }}
                    </span>
                    <div v-if="isScheduleExpired(schedule)" class="text-[11px] text-amber-700 mt-1">
                      انتهت المدة (مخفي للمستخدمين بالتطبيق)
                    </div>
                  </td>
                  <td class="px-6 py-4 whitespace-nowrap text-sm font-medium">
                    <div class="flex gap-2">
                      <!--
                      <button 
                        @click="openEditModal(schedule.id)"
                        class="text-blue-600 hover:text-blue-900 bg-blue-50 hover:bg-blue-100 px-3 py-1 rounded"
                      >
                        تعديل
                      </button>
                    -->
                      <button 
                        @click="deleteSchedule(schedule)"
                        class="text-red-600 hover:text-red-900 bg-red-50 hover:bg-red-100 px-3 py-1 rounded"
                      >
                        حذف
                      </button>
                    </div>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
        </template>
      </div>

      <!-- Empty State (Table View) -->
      <div v-else-if="viewMode === 'table' && selectedTeacherId && !loading && filteredSchedules.length === 0" class="text-center py-12 bg-white rounded-lg shadow">
        <div class="mx-auto h-12 w-12 text-gray-400">
          <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" 
                  d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2"/>
          </svg>
        </div>
        <h3 class="mt-2 text-sm font-medium text-gray-900">لا توجد جداول</h3>
        <p class="mt-1 text-sm text-gray-500" v-if="filterActive !== 'all'">لا توجد جداول تطابق الفلتر المحدد</p>
        <p class="mt-1 text-sm text-gray-500" v-else>ابدأ بإضافة جدول جديد للمعلم</p>
      </div>

      <!-- Empty State (Grouped View) -->
      <div v-else-if="viewMode === 'grouped' && selectedTeacherId && !loading && (!orderedSchedulesByGroup || Object.keys(orderedSchedulesByGroup).length === 0)" class="text-center py-12 bg-white rounded-lg shadow">
        <div class="mx-auto h-12 w-12 text-gray-400">
          <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" 
                  d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2"/>
          </svg>
        </div>
        <h3 class="mt-2 text-sm font-medium text-gray-900">لا توجد جداول</h3>
        <p class="mt-1 text-sm text-gray-500">ابدأ بإضافة جدول جديد للمعلم</p>
      </div>

      <!-- Create/Edit Modal -->
      <div v-if="showModal" class="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50" @click.self="closeModal">
        <div class="relative top-10 mx-auto p-5 border w-full max-w-4xl shadow-lg rounded-2xl bg-white mb-10">
          <div class="mt-3">
            <div class="flex justify-between items-center mb-4">
              <h3 class="text-lg font-medium text-gray-900">
                {{ editingSchedule ? 'تعديل الجدول' : 'إضافة جدول جديد' }}
              </h3>
              <div v-if="!editingSchedule" class="flex gap-2">
                <button
                  @click="useCompleteForm = true"
                  :class="useCompleteForm ? 'bg-blue-600 text-white' : 'bg-gray-200 text-gray-700'"
                  class="px-3 py-1 rounded text-sm"
                >
                  إنشاء سريع
                </button>
                <button
                  @click="useCompleteForm = false"
                  :class="!useCompleteForm ? 'bg-blue-600 text-white' : 'bg-gray-200 text-gray-700'"
                  class="px-3 py-1 rounded text-sm"
                >
                  إنشاء تقليدي
                </button>
              </div>
              <div v-else class="text-xs text-blue-700 bg-blue-50 border border-blue-100 px-3 py-1 rounded">
                يتم تعبئة بيانات السجل الحالي للتعديل المباشر
              </div>
            </div>
            
            <!-- Complete Form (One Step) -->
            <form v-if="useCompleteForm && !editingSchedule" @submit.prevent="submitCompleteForm" class="space-y-4">
              <div class="rounded-xl border border-slate-200 p-4 bg-slate-50">
                <div class="flex items-center gap-2 mb-3">
                  <span class="w-7 h-7 rounded-full bg-blue-100 text-blue-700 flex items-center justify-center text-xs font-bold">1</span>
                  <h4 class="text-sm font-bold text-slate-800">نوع الجدول</h4>
                </div>
                <div class="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <label
                    class="flex items-center justify-between p-3 border rounded-lg cursor-pointer transition"
                    :class="completeForm.schedule_type === 'regular' ? 'border-blue-500 bg-blue-50' : 'border-slate-200 bg-white'"
                  >
                    <span class="text-sm font-medium text-slate-700">جدول عادي</span>
                    <input v-model="completeForm.schedule_type" value="regular" type="radio" class="text-blue-600 focus:ring-blue-500" />
                  </label>
                  <label
                    class="flex items-center justify-between p-3 border rounded-lg cursor-pointer transition"
                    :class="completeForm.schedule_type === 'custom' ? 'border-blue-500 bg-blue-50' : 'border-slate-200 bg-white'"
                  >
                    <span class="text-sm font-medium text-slate-700">جدول مخصص</span>
                    <input v-model="completeForm.schedule_type" value="custom" type="radio" class="text-blue-600 focus:ring-blue-500" />
                  </label>
                </div>
              </div>

              <!-- Teacher Select -->
              <div>
                <label class="block text-sm font-medium text-gray-700 mb-1">المعلم</label>
                <select 
                  v-model="completeForm.teacher_id" 
                  @change="onTeacherChange"
                  required
                  class="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">اختر المعلم</option>
                  <option v-for="teacher in teachers" :key="teacher.id" :value="teacher.id">
                    {{ teacher.fullName }}
                  </option>
                </select>
              </div>

              <!-- Region Select -->
              <div>
                <label class="block text-sm font-medium text-gray-700 mb-1">المنطقة</label>
                <select 
                  v-model="completeForm.region_id" 
                  @change="onRegionChange"
                  required
                  class="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">اختر المنطقة</option>
                  <option v-for="region in regions" :key="region.id" :value="region.id">
                    {{ region.regionNameAr || region.region_name_ar || region.regionNameEn || region.region_name_en }}
                  </option>
                </select>
              </div>

              <!-- Prayer Select -->
              <div>
                <label class="block text-sm font-medium text-gray-700 mb-1">الصلاة</label>
                <select 
                  v-model="completeForm.prayer_name" 
                  @change="onPrayerChange"
                  required
                  :disabled="!completeForm.region_id || loadingPrayerTime"
                  class="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">اختر الصلاة</option>
                  <option value="FAJR">الفجر</option>
                  <option value="DHUHR">الظهر</option>
                  <option value="ASR">العصر</option>
                  <option value="MAGHRIB">المغرب</option>
                  <option value="ISHA">العشاء</option>
                </select>
                <div v-if="loadingPrayerTime" class="text-xs text-blue-600 mt-1">
                  جاري جلب وقت الصلاة...
                </div>
                <div v-if="completeForm.prayer_time && !loadingPrayerTime" class="text-xs text-green-600 mt-1">
                  ✓ وقت الصلاة: {{ formatTime(completeForm.prayer_time) }}
                </div>
              </div>

              <!-- First Session Start Minutes After Prayer -->
              <div>
                <label class="block text-sm font-medium text-gray-700 mb-1">وقت بداية الجلسة الأولى بعد الصلاة (بالدقائق)</label>
                <input 
                  v-model.number="completeForm.session_start_after_minutes" 
                  type="number" 
                  min="0" 
                  required
                  class="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
                />
                <p class="text-xs text-gray-500 mt-1">
                  مثال: 0 = مباشرة بعد الصلاة، 15 = بعد 15 دقيقة من الصلاة
                </p>
              </div>

              <!-- Session Count -->
              <div>
                <label class="block text-sm font-medium text-gray-700 mb-1">عدد الجلسات</label>
                <input 
                  v-model.number="completeForm.session_count" 
                  type="number" 
                  min="1" 
                  required
                  class="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <!-- Session Duration -->
              <div>
                <label class="block text-sm font-medium text-gray-700 mb-1">مدة الجلسة (بالدقائق)</label>
                <input 
                  v-model.number="completeForm.session_duration_minutes" 
                  type="number" 
                  min="1" 
                  required
                  class="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <!-- Session Name (Optional) -->
              <div>
                <label class="block text-sm font-medium text-gray-700 mb-1">اسم الجلسة (اختياري - سيتم إنشاؤها تلقائياً إذا لم تكن موجودة)</label>
                <input 
                  v-model="completeForm.sessions_name" 
                  type="text" 
                  class="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
                  placeholder="مثال: جلسة الحفظ"
                />
              </div>

              <!-- Day of Week -->
              <div>
                <label class="block text-sm font-medium text-gray-700 mb-1">يوم الأسبوع</label>
                <div class="space-y-2">
                  <label class="flex items-center">
                    <input 
                      type="checkbox"
                      :checked="isAllDaysSelected"
                      @change="handleCompleteAllDaysChange"
                      class="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                    />
                    <span class="mr-2 text-sm text-gray-700">كل الأيام</span>
                  </label>
                  <div class="grid grid-cols-2 gap-2">
                    <label class="flex items-center" v-for="(day, index) in daysOfWeek" :key="index">
                      <input 
                        type="checkbox"
                        :value="index"
                        v-model="completeForm.selected_days"
                        :disabled="isAllDaysSelected"
                        @change="handleDaySelectionChange"
                        class="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                      />
                      <span class="mr-2 text-sm text-gray-700">{{ day }}</span>
                    </label>
                  </div>
                  <p v-if="!isAllDaysSelected && completeForm.selected_days && completeForm.selected_days.length > 0" class="text-xs text-gray-500 mt-1">
                    تم اختيار {{ completeForm.selected_days.length }} يوم/أيام
                  </p>
                </div>
              </div>

              <div v-if="completeForm.schedule_type === 'custom'" class="rounded-xl border border-blue-100 p-4 bg-blue-50/60">
                <div class="flex items-center gap-2 mb-4">
                  <span class="w-7 h-7 rounded-full bg-blue-100 text-blue-700 flex items-center justify-center text-xs font-bold">2</span>
                  <h4 class="text-sm font-bold text-slate-800">المدة الزمنية للجدول المخصص</h4>
                </div>
                <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label class="block text-sm font-medium text-gray-700 mb-1">تاريخ البداية (start_date)</label>
                    <input
                      v-model="completeForm.start_date"
                      type="date"
                      :min="todayDate"
                      class="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 bg-white"
                    />
                  </div>
                  <div>
                    <label class="block text-sm font-medium text-gray-700 mb-1">تاريخ النهاية (end_date)</label>
                    <input
                      v-model="completeForm.end_date"
                      type="date"
                      :min="completeForm.start_date || todayDate"
                      class="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 bg-white"
                    />
                  </div>
                </div>
                <label class="flex items-center mt-4">
                  <input
                    v-model="completeForm.archive_after_end"
                    type="checkbox"
                    class="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                  />
                  <span class="mr-2 text-sm text-gray-700">أرشفة بعد انتهاء المدة (اختياري)</span>
                </label>
                <p class="text-xs text-blue-700 mt-2">
                  بعد تجاوز تاريخ النهاية، سيتم إخفاء الجدول من التطبيق للمستخدمين، مع إمكانية الاحتفاظ به للأرشفة.
                </p>
              </div>

              <!-- Is Active -->
              <div>
                <label class="flex items-center">
                  <input 
                    v-model="completeForm.is_active" 
                    type="checkbox" 
                    class="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                  />
                  <span class="mr-2 text-sm text-gray-700">نشط</span>
                </label>
              </div>

              <!-- Priority -->
              <div>
                <label class="block text-sm font-medium text-gray-700 mb-1">الأولوية (0 = أعلى أولوية)</label>
                <input 
                  v-model.number="completeForm.priority" 
                  type="number" 
                  min="0" 
                  class="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <!-- Error Message -->
              <div v-if="errorMessage" class="p-3 bg-red-50 border border-red-200 rounded-md">
                <p class="text-sm text-red-600">{{ errorMessage }}</p>
              </div>

              <!-- Actions -->
              <div class="flex gap-2 pt-4">
                <button 
                  type="submit" 
                  :disabled="submitting || loadingPrayerTime"
                  class="flex-1 bg-blue-600 hover:bg-blue-700 disabled:bg-blue-400 text-white px-4 py-2 rounded-lg"
                >
                  {{ submitting ? 'جاري الحفظ...' : 'إنشاء الجدول' }}
                </button>
                <button 
                  type="button" 
                  @click="closeModal"
                  class="flex-1 bg-gray-300 hover:bg-gray-400 text-gray-800 px-4 py-2 rounded-lg"
                >
                  إلغاء
                </button>
              </div>
            </form>

            <!-- Traditional Form -->
            <form v-else @submit.prevent="submitForm" class="space-y-4">
              <!-- Session Select -->
              <div>
                <label class="block text-sm font-medium text-gray-700 mb-1">الجلسة</label>
                <select 
                  v-model="form.sessions_id" 
                  required
                  class="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">اختر الجلسة</option>
                  <option v-for="session in teacherSessions" :key="session.id" :value="session.id">
                    {{ session.sessionsName || session.sessions_name || `جلسة ${session.id}` }}
                  </option>
                </select>
                <p v-if="teacherSessions.length === 0" class="text-xs text-red-500 mt-1">
                  ⚠️ لا توجد جلسات متاحة لهذا المعلم. يرجى التأكد من أن المعلم لديه جلسة نشطة.
                </p>
                <p v-else class="text-xs text-gray-500 mt-1">
                  {{ teacherSessions.length }} جلسة متاحة
                </p>
              </div>

              <!-- Region Select -->
              <div>
                <label class="block text-sm font-medium text-gray-700 mb-1">المنطقة</label>
                <select
                  v-model="form.region_id"
                  @change="onTraditionalRegionChange"
                  class="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">اختر المنطقة</option>
                  <option v-for="region in regions" :key="region.id" :value="region.id">
                    {{ region.regionNameAr || region.region_name_ar || region.regionNameEn || region.region_name_en }}
                  </option>
                </select>
              </div>

              <!-- Slot Builder -->
              <div class="rounded-xl border border-slate-200 p-4 bg-slate-50">
                <div class="flex items-center justify-between mb-3">
                  <h4 class="text-sm font-bold text-slate-800">الشرائح الزمنية</h4>
                  <button
                    type="button"
                    @click="addSlot"
                    class="px-3 py-1.5 rounded-lg bg-blue-600 hover:bg-blue-700 text-white text-sm"
                  >
                    + إضافة شريحة
                  </button>
                </div>
                <div class="space-y-3">
                  <div
                    v-for="(slot, index) in form.slots"
                    :key="slot.id"
                    class="bg-white border border-slate-200 rounded-xl p-4"
                  >
                    <div class="flex items-center justify-between mb-3">
                      <p class="text-sm font-semibold text-slate-700">الشريحة {{ index + 1 }}</p>
                      <button
                        type="button"
                        @click="removeSlot(index)"
                        :disabled="form.slots.length === 1"
                        class="text-red-600 hover:text-red-700 disabled:text-gray-400 text-sm"
                      >
                        حذف
                      </button>
                    </div>
                    <div class="grid grid-cols-1 md:grid-cols-2 gap-3">
                      <div>
                        <label class="block text-xs font-medium text-gray-700 mb-1">طريقة تحديد الوقت</label>
                        <select
                          v-model="slot.time_mode"
                          class="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
                        >
                          <option value="preset">وقت جاهز من القائمة</option>
                          <option value="manual">وقت يدوي</option>
                          <option value="after_prayer">بعد الصلاة</option>
                        </select>
                      </div>
                      <div>
                        <label class="block text-xs font-medium text-gray-700 mb-1">اليوم</label>
                        <select
                          v-model="slot.day_of_week"
                          class="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
                        >
                          <option value="all">كل الأيام</option>
                          <option v-for="(day, dayIndex) in daysOfWeek" :key="dayIndex" :value="dayIndex">
                            {{ day }}
                          </option>
                        </select>
                      </div>
                      <div v-if="slot.time_mode === 'preset'">
                        <label class="block text-xs font-medium text-gray-700 mb-1">وقت الجلسة</label>
                        <select
                          v-model="slot.session_time_id"
                          class="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
                        >
                          <option value="">اختر وقت الجلسة</option>
                          <option v-for="time in sessionTimes" :key="time.id" :value="time.id">
                            {{ getPrayerNameArabic(time.prayerName) }} - {{ formatTime(time.sessionStartTime) }} - {{ formatTime(time.sessionEndTime) }}
                          </option>
                        </select>
                      </div>
                      <template v-if="slot.time_mode === 'manual'">
                        <div>
                          <label class="block text-xs font-medium text-gray-700 mb-1">window_start_time</label>
                          <input
                            v-model="slot.window_start_time"
                            type="time"
                            class="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
                          />
                        </div>
                        <div>
                          <label class="block text-xs font-medium text-gray-700 mb-1">window_end_time</label>
                          <input
                            v-model="slot.window_end_time"
                            type="time"
                            class="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
                          />
                        </div>
                      </template>
                      <template v-if="slot.time_mode === 'after_prayer'">
                        <div>
                          <label class="block text-xs font-medium text-gray-700 mb-1">الصلاة</label>
                          <select
                            v-model="slot.prayer_name"
                            class="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
                          >
                            <option value="">اختر الصلاة</option>
                            <option value="FAJR">الفجر</option>
                            <option value="DHUHR">الظهر</option>
                            <option value="ASR">العصر</option>
                            <option value="MAGHRIB">المغرب</option>
                            <option value="ISHA">العشاء</option>
                          </select>
                        </div>
                        <div>
                          <label class="block text-xs font-medium text-gray-700 mb-1">كم دقيقة بعد الأذان</label>
                          <input
                            v-model.number="slot.minutes_after_adhan"
                            type="number"
                            min="0"
                            class="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
                          />
                        </div>
                      </template>
                      <div>
                        <label class="block text-xs font-medium text-gray-700 mb-1">عدد الجلسات</label>
                        <input
                          v-model.number="slot.session_count"
                          type="number"
                          min="1"
                          class="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
                        />
                      </div>
                      <div>
                        <label class="block text-xs font-medium text-gray-700 mb-1">مدة الجلسة (بالدقائق)</label>
                        <input
                          v-model.number="slot.session_duration_minutes"
                          type="number"
                          min="1"
                          class="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
                        />
                      </div>
                    </div>
                    <div
                      v-if="slot.time_mode === 'preset' && slot.session_count && slot.session_duration_minutes && getSlotAvailableWindowMinutes(slot) > 0"
                      class="mt-3 p-2 rounded-md bg-blue-50 text-xs text-blue-800"
                    >
                      الإجمالي: {{ slot.session_count * slot.session_duration_minutes }} دقيقة -
                      النافذة المتاحة: {{ getSlotAvailableWindowMinutes(slot) }} دقيقة
                    </div>
                    <p v-if="slot.time_mode === 'manual'" class="mt-2 text-xs text-amber-700">
                      ملاحظة: في وضع `manual` لن يتم إنشاء `master_session_time` جديد. سيتم استخدام مرجع موجود فقط مع حفظ `window_*` في جدول المعلم.
                    </p>
                  </div>
                </div>
                <p v-if="sessionTimes.length === 0" class="text-xs text-red-500 mt-3">
                  ⚠️ لا توجد أوقات جلسات متاحة. يرجى التأكد من وجود أوقات جلسات في المنطقة.
                </p>
              </div>

              <!-- Is Active -->
              <div>
                <label class="flex items-center">
                  <input 
                    v-model="form.is_active" 
                    type="checkbox" 
                    class="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                  />
                  <span class="mr-2 text-sm text-gray-700">نشط</span>
                </label>
              </div>

              <!-- Priority -->
              <div>
                <label class="block text-sm font-medium text-gray-700 mb-1">الأولوية (0 = أعلى أولوية)</label>
                <input 
                  v-model.number="form.priority" 
                  type="number" 
                  min="0" 
                  class="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <!-- Error Message -->
              <div v-if="errorMessage" class="p-3 bg-red-50 border border-red-200 rounded-md">
                <p class="text-sm text-red-600">{{ errorMessage }}</p>
              </div>

              <!-- Actions -->
              <div class="flex gap-2 pt-4">
                <button 
                  type="submit" 
                  :disabled="submitting"
                  class="flex-1 bg-blue-600 hover:bg-blue-700 disabled:bg-blue-400 text-white px-4 py-2 rounded-lg"
                >
                  {{ submitting ? 'جاري الحفظ...' : 'حفظ' }}
                </button>
                <button 
                  type="button" 
                  @click="closeModal"
                  class="flex-1 bg-gray-300 hover:bg-gray-400 text-gray-800 px-4 py-2 rounded-lg"
                >
                  إلغاء
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  </main>
</template>

<script>
import { defineComponent } from 'vue';
import axios from 'axios';
import url from '@/url';
import * as teacherScheduleService from '@/services/teacherSchedule';

export default defineComponent({
  name: 'TeacherScheduleManagement',
  data() {
    return {
      teachers: [],
      selectedTeacherId: '',
      schedules: [],
      schedulesByDay: null,
      schedulesByGroup: null,
      loading: false,
      showModal: false,
      editingSchedule: null,
      submitting: false,
      errorMessage: '',
      teacherSessions: [],
      sessionTimes: [],
      viewMode: 'table', // 'table' or 'grouped'
      filterActive: 'all', // 'all', 'true', 'false'
      useCompleteForm: true, // Toggle between complete and traditional form
      regions: [],
      loadingPrayerTime: false,
      daysOfWeek: ['الأحد', 'الإثنين', 'الثلاثاء', 'الأربعاء', 'الخميس', 'الجمعة', 'السبت'],
      form: {
        sessions_id: '',
        region_id: '',
        session_start_after_minutes: 0,
        slots: [
          {
            id: 1,
            time_mode: 'preset',
            session_time_id: '',
            day_of_week: 'all',
            window_start_time: '',
            window_end_time: '',
            prayer_name: '',
            minutes_after_adhan: 0,
            session_count: 1,
            session_duration_minutes: 30,
          },
        ],
        is_active: true,
        priority: 0,
      },
      completeForm: {
        teacher_id: '',
        region_id: '',
        prayer_name: '',
        prayer_time: '',
        schedule_type: 'regular',
        start_date: '',
        end_date: '',
        archive_after_end: true,
        first_session_start_minutes_after_prayer: 0,
        session_start_after_minutes: 0,
        session_count: 1,
        session_duration_minutes: 30,
        sessions_name: '',
        selected_days: [],
        is_active: true,
        priority: 0,
      },
    };
  },
  computed: {
    todayDate() {
      return new Date().toISOString().slice(0, 10);
    },
    filteredSchedules() {
      if (!this.schedules || this.schedules.length === 0) return [];
      if (this.filterActive === 'all') return this.schedules;
      const isActive = this.filterActive === 'true';
      return this.schedules.filter(s => s.isActive === isActive);
    },
    orderedSchedulesByDay() {
      if (!this.schedulesByDay) return {};
      
      // Define the order: all_days first, then days of week
      const dayOrder = ['all_days', 'sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
      const ordered = {};
      
      // First, add all_days if it exists and has items
      if (this.schedulesByDay.all_days && this.schedulesByDay.all_days.length > 0) {
        ordered.all_days = this.schedulesByDay.all_days;
      }
      
      // Then add other days in order
      dayOrder.forEach(dayKey => {
        if (dayKey !== 'all_days' && this.schedulesByDay[dayKey] && this.schedulesByDay[dayKey].length > 0) {
          ordered[dayKey] = this.schedulesByDay[dayKey];
        }
      });
      
      return ordered;
    },
    orderedSchedulesByGroup() {
      if (!this.schedulesByGroup) return {};
      
      // Sort groups by scheduleGroupId (ascending)
      const sortedGroupIds = Object.keys(this.schedulesByGroup).sort((a, b) => {
        const idA = parseInt(a) || 0;
        const idB = parseInt(b) || 0;
        return idA - idB;
      });
      
      const ordered = {};
      sortedGroupIds.forEach(groupId => {
        if (this.schedulesByGroup[groupId] && this.schedulesByGroup[groupId].length > 0) {
          ordered[groupId] = this.schedulesByGroup[groupId];
        }
      });
      
      return ordered;
    },
  },
  async mounted() {
    await this.loadTeachers();
  },
  methods: {
    async loadTeachers() {
      try {
        const response = await axios.get(
          process.env.VUE_APP_SERVICE_URL + url.get_all_teachers,
          {
            headers: { 'X-Authorization': localStorage.getItem('token') },
            params: { enabled: true },
          }
        );
        if (response.data?.result?.data) {
          this.teachers = response.data.result.data;
        }
      } catch (error) {
        console.error('Error loading teachers:', error);
        alert('فشل في تحميل قائمة المعلمين');
      }
    },
    async loadRegions() {
      try {
        const response = await axios.get(
          process.env.VUE_APP_SERVICE_URL + url.master_regions,
          {
            headers: { 'X-Authorization': localStorage.getItem('token') },
          }
        );
        if (response.data?.result?.data) {
          this.regions = response.data.result.data;
        }
      } catch (error) {
        console.error('Error loading regions:', error);
        this.regions = [];
      }
    },
    async loadSchedules() {
      if (!this.selectedTeacherId) {
        this.schedules = [];
        this.schedulesByDay = null;
        this.schedulesByGroup = null;
        return;
      }
      
      this.loading = true;
      try {
        // Get all schedules (not grouped by day, we'll group by scheduleGroupId)
        const result = await teacherScheduleService.getTeacherSchedules(this.selectedTeacherId);
        this.schedules = result.data || [];
        
        // Group schedules by scheduleGroupId (this is the main grouping method)
        this.schedulesByGroup = this.groupSchedulesByGroupId(this.schedules);
        
        // Keep schedulesByDay for backward compatibility (if needed)
        this.schedulesByDay = null;
        
        await this.loadTeacherSessions();
      } catch (error) {
        console.error('Error loading schedules:', error);
        alert('فشل في تحميل الجداول');
      } finally {
        this.loading = false;
      }
    },
    groupSchedulesByGroupId(schedules) {
      const grouped = {};
      
      schedules.forEach(schedule => {
        // Use scheduleGroupId (from backend) or schedule_group_id (alternative field name)
        // If not available, use schedule id as fallback for backward compatibility
        const groupId = schedule.scheduleGroupId || schedule.schedule_group_id || schedule.id || 'no-group';
        
        // Convert to string for consistent key usage
        const groupKey = String(groupId);
        
        if (!grouped[groupKey]) {
          grouped[groupKey] = [];
        }
        
        grouped[groupKey].push(schedule);
      });
      
      return grouped;
    },
    toggleViewMode() {
      this.viewMode = this.viewMode === 'table' ? 'grouped' : 'table';
    },
    applyFilter() {
      // Filter is applied via computed property
      // This method is here for potential future enhancements
    },
    getDayLabel(dayKey) {
      const labels = {
        'all_days': 'كل الأيام',
        'sunday': 'الأحد',
        'monday': 'الإثنين',
        'tuesday': 'الثلاثاء',
        'wednesday': 'الأربعاء',
        'thursday': 'الخميس',
        'friday': 'الجمعة',
        'saturday': 'السبت',
      };
      return labels[dayKey] || dayKey;
    },
    async loadTeacherSessions() {
      if (!this.selectedTeacherId) {
        this.teacherSessions = [];
        return;
      }
      
      try {
        const response = await axios.get(
          process.env.VUE_APP_SERVICE_URL + url.master_sessions,
          {
            headers: { 'X-Authorization': localStorage.getItem('token') },
          }
        );
        
        console.log('Sessions API Response:', response.data);
        
        if (response.data?.result?.data) {
          const allSessions = response.data.result.data;
          console.log('All sessions from API:', allSessions);
          console.log('Selected teacher ID:', this.selectedTeacherId);
          
          // Try different field names for teacher ID
          this.teacherSessions = allSessions.filter(session => {
            // Try multiple possible field names
            const teacherId = session.userId || session.user_id || session.userId;
            const matches = teacherId === parseInt(this.selectedTeacherId) || 
                           teacherId == this.selectedTeacherId ||
                           String(teacherId) === String(this.selectedTeacherId);
            
            console.log('Session filter check:', {
              sessionId: session.id,
              sessionName: session.sessionsName,
              sessionUserId: teacherId,
              selectedTeacherId: this.selectedTeacherId,
              matches
            });
            
            return matches;
          });
          
          console.log('Filtered teacher sessions (by ID):', this.teacherSessions);
          
          // If no sessions found by ID, try alternative approach by name
          if (this.teacherSessions.length === 0) {
            const teacher = this.teachers.find(t => t.id === parseInt(this.selectedTeacherId));
            if (teacher && teacher.fullName) {
              this.teacherSessions = allSessions.filter(session => {
                const matches = session.sessionsName && 
                               (session.sessionsName.includes(teacher.fullName) ||
                                session.sessionsName === `جلسة ${teacher.fullName}` ||
                                session.sessionsName === `Session ${teacher.fullName}`);
                if (matches) {
                  console.log('Found session by name:', session);
                }
                return matches;
              });
              console.log('Found sessions by name:', this.teacherSessions);
            }
          }
          
          // If still no sessions, show all sessions for debugging
          if (this.teacherSessions.length === 0) {
            console.warn('No sessions found for teacher. All available sessions:', allSessions);
            console.warn('Available teacher IDs in sessions:', allSessions.map(s => ({
              id: s.id,
              name: s.sessionsName,
              userId: s.userId || s.user_id
            })));
          }
        } else {
          console.warn('No sessions data in response:', response.data);
          this.teacherSessions = [];
        }
      } catch (error) {
        console.error('Error loading teacher sessions:', error);
        this.teacherSessions = [];
      }
    },
    async loadSessionTimes(regionIdOverride = null) {
      if (!this.selectedTeacherId) {
        this.sessionTimes = [];
        return;
      }
      
      const teacher = this.teachers.find(t => t.id === parseInt(this.selectedTeacherId));
      if (!teacher) {
        console.warn('Teacher not found for ID:', this.selectedTeacherId);
        this.sessionTimes = [];
        return;
      }

      // Try to get regionId from teacher, or try to get it from teacher's session
      let regionId = regionIdOverride || teacher.regionId;
      
      // If regionId is not available, try to get it from teacher's session
      if (!regionId && this.teacherSessions.length > 0) {
        // Try to get region from first session
        const firstSession = this.teacherSessions[0];
        if (firstSession.regionId) {
          regionId = firstSession.regionId;
        }
      }

      if (!regionId) {
        console.warn('No regionId found for teacher:', teacher);
        // Try to load from all regions if teacher region is not available
        await this.loadSessionTimesFromAllRegions();
        return;
      }
      
      try {
        console.log('Loading session times for region:', regionId);
        const sessionTimesResponse = await axios.get(
          process.env.VUE_APP_SERVICE_URL + url.get_session_times + regionId,
          {
            headers: { 'X-Authorization': localStorage.getItem('token') },
          }
        );
        
        console.log('Session Times API Response:', sessionTimesResponse.data);
        
        if (sessionTimesResponse.data?.result?.data) {
          this.sessionTimes = sessionTimesResponse.data.result.data;
          console.log('Loaded session times:', this.sessionTimes.length);
        } else {
          console.warn('No session times data in response');
          this.sessionTimes = [];
        }
      } catch (error) {
        console.error('Error loading session times:', error);
        this.sessionTimes = [];
        // Try to load from all regions as fallback
        await this.loadSessionTimesFromAllRegions();
      }
    },
    async loadSessionTimesFromAllRegions() {
      // Fallback: Try to load session times from all regions
      try {
        console.log('Attempting to load session times from all regions...');
        // Get all regions first
        const regionsResponse = await axios.get(
          process.env.VUE_APP_SERVICE_URL + url.master_regions,
          {
            headers: { 'X-Authorization': localStorage.getItem('token') },
          }
        );
        
        if (regionsResponse.data?.result?.data && regionsResponse.data.result.data.length > 0) {
          const regions = regionsResponse.data.result.data;
          const allSessionTimes = [];
          
          // Load session times from all regions
          for (const region of regions) {
            try {
              const sessionTimesResponse = await axios.get(
                process.env.VUE_APP_SERVICE_URL + url.get_session_times + region.id,
                {
                  headers: { 'X-Authorization': localStorage.getItem('token') },
                }
              );
              
              if (sessionTimesResponse.data?.result?.data) {
                allSessionTimes.push(...sessionTimesResponse.data.result.data);
              }
            } catch (err) {
              console.warn(`Failed to load session times for region ${region.id}:`, err);
            }
          }
          
          if (allSessionTimes.length > 0) {
            // Remove duplicates based on id
            this.sessionTimes = allSessionTimes.filter((time, index, self) =>
              index === self.findIndex(t => t.id === time.id)
            );
            console.log('Loaded session times from all regions:', this.sessionTimes.length);
          } else {
            this.sessionTimes = [];
          }
        }
      } catch (error) {
        console.error('Error loading session times from all regions:', error);
        this.sessionTimes = [];
      }
    },
    async openCreateModal() {
      this.editingSchedule = null;
      this.useCompleteForm = true;
      this.form = {
        sessions_id: '',
        region_id: this.getSelectedTeacherRegionId() || '',
        session_start_after_minutes: 0,
        slots: [this.createDefaultSlot()],
        is_active: true,
        priority: 0,
      };
      this.completeForm = {
        teacher_id: this.selectedTeacherId || '',
        region_id: '',
        prayer_name: '',
        prayer_time: '',
        schedule_type: 'regular',
        start_date: '',
        end_date: '',
        archive_after_end: true,
        first_session_start_minutes_after_prayer: 0,
        session_start_after_minutes: 0,
        session_count: 1,
        session_duration_minutes: 30,
        sessions_name: '',
        selected_days: [],
        is_active: true,
        priority: 0,
      };
      this.errorMessage = '';
      await Promise.all([
        this.loadRegions(),
        this.loadTeacherSessions(),
        this.loadSessionTimes()
      ]);
      this.showModal = true;
    },
    async openEditModal(scheduleId) {
      this.loading = true;
      this.errorMessage = '';
      try {
        const schedule = await teacherScheduleService.getTeacherSchedule(this.selectedTeacherId, scheduleId);
        if (!schedule) {
          throw new Error('تعذر جلب بيانات الجدول للتعديل');
        }

        this.editingSchedule = schedule;
        this.useCompleteForm = false;
        this.completeForm = {
          teacher_id: this.selectedTeacherId || '',
          region_id: this.getSelectedTeacherRegionId() || '',
          prayer_name: schedule.prayerName || '',
          prayer_time: '',
          schedule_type: this.getScheduleType(schedule),
          start_date: schedule.startDate || '',
          end_date: schedule.endDate || '',
          archive_after_end: schedule.archiveAfterEnd ?? true,
          first_session_start_minutes_after_prayer: schedule.sessionStartAfterMinutes ?? 0,
          session_count: schedule.sessionCount ?? 1,
          session_duration_minutes: schedule.sessionDurationMinutes ?? 30,
          sessions_name: schedule.sessionName || '',
          selected_days: [],
          is_active: schedule.isActive ?? true,
          priority: schedule.priority ?? 0,
          session_start_after_minutes: schedule.sessionStartAfterMinutes ?? 0,
        };
        this.form = {
          sessions_id: schedule.sessionId,
          region_id: this.getSelectedTeacherRegionId() || '',
          session_start_after_minutes: schedule.sessionStartAfterMinutes ?? 0,
          slots: [
            {
              id: Date.now(),
              time_mode: 'preset',
              session_time_id: schedule.sessionTimeId,
              day_of_week: schedule.dayOfWeek === null ? 'all' : schedule.dayOfWeek,
              window_start_time: this.normalizeToHourMinute(schedule.windowStartTime || schedule.sessionStartTime),
              window_end_time: this.normalizeToHourMinute(schedule.windowEndTime || schedule.sessionEndTime),
              prayer_name: schedule.prayerName || '',
              minutes_after_adhan: schedule.sessionStartAfterMinutes ?? 0,
              session_count: schedule.sessionCount,
              session_duration_minutes: schedule.sessionDurationMinutes,
            },
          ],
          is_active: schedule.isActive,
          priority: schedule.priority,
        };

        await this.loadSessionTimes();
        this.showModal = true;
      } catch (error) {
        this.errorMessage = error.message || 'فشل في تحميل بيانات الجدول';
        alert(this.errorMessage);
      } finally {
        this.loading = false;
      }
    },
    createDefaultSlot() {
      return {
        id: Date.now() + Math.floor(Math.random() * 100000),
        time_mode: 'preset',
        session_time_id: '',
        day_of_week: 'all',
        window_start_time: '',
        window_end_time: '',
        prayer_name: '',
        minutes_after_adhan: 0,
        session_count: 1,
        session_duration_minutes: 30,
      };
    },
    getSelectedTeacherRegionId() {
      const teacher = this.teachers.find(t => String(t.id) === String(this.selectedTeacherId));
      return teacher?.regionId || teacher?.region_id || null;
    },
    normalizeToHourMinute(timeValue) {
      if (!timeValue || typeof timeValue !== 'string') return '';
      const parts = timeValue.split(':');
      if (parts.length < 2) return '';
      const hours = String(parts[0]).padStart(2, '0');
      const minutes = String(parts[1]).padStart(2, '0');
      return `${hours}:${minutes}`;
    },
    findSessionTimeIdByManualWindow(slot) {
      const start = this.normalizeToHourMinute(slot.window_start_time);
      const end = this.normalizeToHourMinute(slot.window_end_time);
      if (!start || !end) return null;
      const matched = this.sessionTimes.find(st => {
        const stStart = this.normalizeToHourMinute(st.sessionStartTime);
        const stEnd = this.normalizeToHourMinute(st.sessionEndTime);
        return stStart === start && stEnd === end;
      });
      return matched?.id || null;
    },
    resolveReferenceSessionTimeIdForWindow(slot) {
      if (slot.session_time_id) return parseInt(slot.session_time_id);

      const exactWindowMatch = this.findSessionTimeIdByManualWindow(slot);
      if (exactWindowMatch) return exactWindowMatch;

      const athanTimeId = this.inferAthanTimeIdForManualWindow(slot);
      if (athanTimeId) {
        const fallbackByAthan = this.sessionTimes
          .filter(st => Number(st?.athanTimeId) === Number(athanTimeId))
          .sort((a, b) => (Number(a?.sessionOrder || 0) - Number(b?.sessionOrder || 0)) || (Number(a?.id || 0) - Number(b?.id || 0)))[0];

        if (fallbackByAthan?.id) return Number(fallbackByAthan.id);
      }

      if (this.editingSchedule?.sessionTimeId) return Number(this.editingSchedule.sessionTimeId);

      return null;
    },
    inferAthanTimeIdForManualWindow(slot) {
      const start = this.normalizeToHourMinute(slot.window_start_time);
      if (!start) return null;

      const toMinutes = (timeValue) => {
        const normalized = this.normalizeToHourMinute(timeValue);
        if (!normalized) return null;
        const [h, m] = normalized.split(':').map(Number);
        if (Number.isNaN(h) || Number.isNaN(m)) return null;
        return (h * 60) + m;
      };

      const startMinutes = toMinutes(start);
      if (startMinutes === null) return null;

      const athanCandidatesMap = new Map();
      this.sessionTimes.forEach((sessionTime) => {
        const athanTimeId = sessionTime?.athanTimeId;
        const prayerTime = sessionTime?.prayerTime;
        const prayerMinutes = toMinutes(prayerTime);
        if (!athanTimeId || prayerMinutes === null) return;
        if (!athanCandidatesMap.has(athanTimeId)) {
          athanCandidatesMap.set(athanTimeId, {
            athanTimeId,
            prayerMinutes,
          });
        }
      });

      const candidates = Array.from(athanCandidatesMap.values())
        .sort((a, b) => a.prayerMinutes - b.prayerMinutes);

      if (candidates.length === 0) return null;

      if (startMinutes < candidates[0].prayerMinutes) {
        return candidates[0].athanTimeId;
      }

      for (let i = 0; i < candidates.length; i++) {
        const current = candidates[i];
        const next = candidates[i + 1];
        if (!next || (startMinutes >= current.prayerMinutes && startMinutes < next.prayerMinutes)) {
          return current.athanTimeId;
        }
      }

      return candidates[candidates.length - 1]?.athanTimeId || null;
    },
    addSlot() {
      this.form.slots.push(this.createDefaultSlot());
    },
    removeSlot(index) {
      if (this.form.slots.length <= 1) return;
      this.form.slots.splice(index, 1);
    },
    getSlotAvailableWindowMinutes(slot) {
      const sessionTime = this.sessionTimes.find(st => st.id === parseInt(slot.session_time_id));
      if (!sessionTime) return 0;
      const start = this.parseTime(sessionTime.sessionStartTime);
      const end = this.parseTime(sessionTime.sessionEndTime);
      if (!start || !end) return 0;
      return Math.round((end - start) / (1000 * 60));
    },
    async submitForm() {
      // Validate form before submission
      if (!this.form.slots || this.form.slots.length === 0) {
        this.errorMessage = 'يرجى إضافة شريحة واحدة على الأقل';
        return;
      }

      const hasNonAfterPrayerSlot = this.form.slots.some(slot => slot.time_mode !== 'after_prayer');
      if (hasNonAfterPrayerSlot && !this.form.sessions_id) {
        this.errorMessage = 'يرجى اختيار الجلسة';
        return;
      }

      for (let i = 0; i < this.form.slots.length; i++) {
        const slot = this.form.slots[i];
        const slotNumber = i + 1;

        if (!slot.time_mode) {
          this.errorMessage = `يرجى اختيار طريقة الوقت في الشريحة ${slotNumber}`;
          return;
        }

        if (slot.time_mode === 'preset' && !slot.session_time_id) {
          this.errorMessage = `يرجى اختيار وقت الجلسة في الشريحة ${slotNumber}`;
          return;
        }

        if (slot.time_mode === 'manual') {
          if (!slot.window_start_time || !slot.window_end_time) {
            this.errorMessage = `يرجى إدخال window_start_time و window_end_time في الشريحة ${slotNumber}`;
            return;
          }
          if (slot.window_end_time <= slot.window_start_time) {
            this.errorMessage = `وقت النهاية يجب أن يكون بعد وقت البداية في الشريحة ${slotNumber}`;
            return;
          }
        }

        if (slot.time_mode === 'after_prayer') {
          if (!slot.prayer_name) {
            this.errorMessage = `يرجى اختيار الصلاة في الشريحة ${slotNumber}`;
            return;
          }
          if (slot.minutes_after_adhan === '' || slot.minutes_after_adhan === null || slot.minutes_after_adhan < 0) {
            this.errorMessage = `يرجى إدخال عدد دقائق صحيح بعد الأذان في الشريحة ${slotNumber}`;
            return;
          }
        }

        if (!slot.session_count || slot.session_count < 1) {
          this.errorMessage = `عدد الجلسات في الشريحة ${slotNumber} يجب أن يكون أكبر من صفر`;
          return;
        }

        if (!slot.session_duration_minutes || slot.session_duration_minutes < 1) {
          this.errorMessage = `مدة الجلسة في الشريحة ${slotNumber} يجب أن تكون أكبر من صفر`;
          return;
        }

        if (slot.time_mode === 'preset') {
          const totalDuration = slot.session_count * slot.session_duration_minutes;
          const availableWindow = this.getSlotAvailableWindowMinutes(slot);
          if (availableWindow > 0 && totalDuration > availableWindow) {
            this.errorMessage = `المدة الإجمالية في الشريحة ${slotNumber} (${totalDuration} دقيقة) تتجاوز النافذة الزمنية المتاحة (${availableWindow} دقيقة)`;
            return;
          }
        }
      }

      this.submitting = true;
      this.errorMessage = '';
      
      try {
        if (this.editingSchedule) {
          // For editing, use first slot only
          const firstSlot = this.form.slots[0];
          let sessionTimeId = firstSlot.session_time_id;

          if (firstSlot.time_mode === 'manual') {
            sessionTimeId = this.resolveReferenceSessionTimeIdForWindow(firstSlot);
            if (!sessionTimeId) {
              throw new Error('تعذر تحديد session_time_id مرجعي لهذه النافذة. اختر وقت جلسة مرجعي من القائمة أو أضف وقتًا عامًا أولاً.');
            }
          }

          if (firstSlot.time_mode === 'after_prayer') {
            this.errorMessage = 'تعديل وضع "بعد الصلاة" غير مدعوم حالياً من شاشة التعديل. أنشئ جدولاً جديداً لهذا النوع.';
            this.submitting = false;
            return;
          }

          const formData = {
            sessions_id: this.form.sessions_id,
            session_time_id: sessionTimeId,
            day_of_week: firstSlot.day_of_week === 'all' ? null : parseInt(firstSlot.day_of_week),
            session_count: firstSlot.session_count,
            session_duration_minutes: firstSlot.session_duration_minutes,
            session_start_after_minutes: this.form.session_start_after_minutes,
            window_start_time: firstSlot.window_start_time || null,
            window_end_time: firstSlot.window_end_time || null,
            is_active: this.form.is_active,
            priority: this.form.priority,
          };
          
          await teacherScheduleService.updateTeacherSchedule(
            this.selectedTeacherId,
            this.editingSchedule.id,
            formData
          );
          alert('تم تحديث الجدول بنجاح');
        } else {
          // For creating, create one schedule per slot
          let createdCount = 0;
          let failedCount = 0;
          const errors = [];
          const teacherRegionId = this.form.region_id || this.getSelectedTeacherRegionId();
          
          for (let i = 0; i < this.form.slots.length; i++) {
            const slot = this.form.slots[i];
            try {
              if (slot.time_mode === 'after_prayer') {
                if (!teacherRegionId) {
                  throw new Error('لا يمكن إنشاء شريحة "بعد الصلاة" بدون منطقة للمعلم');
                }

                const completePayload = {
                  region_id: teacherRegionId,
                  prayer_name: slot.prayer_name,
                  session_count: slot.session_count,
                  session_duration_minutes: slot.session_duration_minutes,
                  first_session_start_minutes_after_prayer: slot.minutes_after_adhan || 0,
                  selected_days: slot.day_of_week === 'all' ? [] : [parseInt(slot.day_of_week)],
                  is_active: this.form.is_active,
                  priority: this.form.priority,
                  schedule_type: 'regular',
                };

                const result = await teacherScheduleService.createTeacherScheduleComplete(
                  this.selectedTeacherId,
                  completePayload
                );
                createdCount += result?.created || 1;
              } else {
                let sessionTimeId = slot.session_time_id;
                if (slot.time_mode === 'manual') {
                  sessionTimeId = this.resolveReferenceSessionTimeIdForWindow(slot);
                  if (!sessionTimeId) {
                    throw new Error('تعذر تحديد session_time_id مرجعي لهذه النافذة. اختر وقت جلسة مرجعي من القائمة أو أضف وقتًا عامًا أولاً.');
                  }
                }

                const formData = {
                  sessions_id: this.form.sessions_id,
                  session_time_id: sessionTimeId,
                  day_of_week: slot.day_of_week === 'all' ? null : parseInt(slot.day_of_week),
                  session_count: slot.session_count,
                  session_duration_minutes: slot.session_duration_minutes,
                  session_start_after_minutes: this.form.session_start_after_minutes,
                  window_start_time: slot.window_start_time || null,
                  window_end_time: slot.window_end_time || null,
                  is_active: this.form.is_active,
                  priority: this.form.priority,
                };

                await teacherScheduleService.createTeacherSchedule(
                  this.selectedTeacherId,
                  formData
                );
                createdCount++;
              }
            } catch (error) {
              failedCount++;
              const dayValue = slot.day_of_week === 'all' ? null : parseInt(slot.day_of_week);
              const dayName = dayValue === null ? 'كل الأيام' : this.getDayName(dayValue);
              errors.push(`الشريحة ${i + 1} (${dayName}): ${error.message || 'فشل في الإنشاء'}`);
            }
          }
          
          if (createdCount > 0) {
            if (failedCount === 0) {
              alert(`تم إنشاء ${createdCount} جدول بنجاح`);
            } else {
              alert(`تم إنشاء ${createdCount} جدول بنجاح، وفشل ${failedCount} جدول:\n${errors.join('\n')}`);
            }
          } else {
            this.errorMessage = `فشل في إنشاء الجداول:\n${errors.join('\n')}`;
            return;
          }
        }
        this.closeModal();
        await this.loadSchedules();
      } catch (error) {
        this.errorMessage = error.message || 'فشل في حفظ الجدول';
      } finally {
        this.submitting = false;
      }
    },
    getDayName(dayOfWeek) {
      const days = ['الأحد', 'الإثنين', 'الثلاثاء', 'الأربعاء', 'الخميس', 'الجمعة', 'السبت'];
      return days[dayOfWeek] || '';
    },
    async deleteSchedule(schedule) {
      if (!confirm(`هل أنت متأكد من حذف هذا الجدول؟`)) return;
      
      try {
        await teacherScheduleService.deleteTeacherSchedule(
          this.selectedTeacherId,
          schedule.id
        );
        alert('تم حذف الجدول بنجاح');
        await this.loadSchedules();
      } catch (error) {
        alert(error.message || 'فشل في حذف الجدول');
      }
    },
    onTeacherChange() {
      // If teacher is selected, optionally pre-fill region from teacher's data
      if (this.completeForm.teacher_id) {
        const teacher = this.teachers.find(t => t.id === parseInt(this.completeForm.teacher_id));
        if (teacher && teacher.regionId && !this.completeForm.region_id) {
          this.completeForm.region_id = teacher.regionId;
        }
      }
    },
    async onRegionChange() {
      this.completeForm.prayer_name = '';
      this.completeForm.prayer_time = '';
      this.loadingPrayerTime = false;
    },
    async onTraditionalRegionChange() {
      this.form.slots = this.form.slots.map(slot => ({
        ...slot,
        session_time_id: '',
      }));
      await this.loadSessionTimes(this.form.region_id || null);
    },
    async onPrayerChange() {
      if (!this.completeForm.region_id || !this.completeForm.prayer_name) {
        this.completeForm.prayer_time = '';
        return;
      }

      this.loadingPrayerTime = true;
      this.completeForm.prayer_time = '';

      try {
        const response = await axios.get(
          `${process.env.VUE_APP_SERVICE_URL}${url.get_prayer_time_from_aladhan}${this.completeForm.region_id}/prayer-time/${this.completeForm.prayer_name}`,
          {
            headers: { 'X-Authorization': localStorage.getItem('token') },
          }
        );

        if (response.data?.result?.status === 200 && response.data?.result?.data?.prayerTime) {
          this.completeForm.prayer_time = response.data.result.data.prayerTime;
        } else {
          this.errorMessage = response.data?.result?.mesAr || 'فشل في جلب وقت الصلاة';
        }
      } catch (error) {
        console.error('Error fetching prayer time:', error);
        this.errorMessage = error.response?.data?.result?.mesAr || 'فشل في جلب وقت الصلاة';
      } finally {
        this.loadingPrayerTime = false;
      }
    },
    handleCompleteAllDaysChange(event) {
      if (event.target.checked) {
        // Select all days = empty array (means all days)
        this.completeForm.selected_days = [];
      } else {
        // If unchecking "all days", don't do anything - let user select specific days
        // Or you could set to first day as default
      }
    },
    handleDaySelectionChange() {
      // When a specific day is selected, ensure "all days" is unchecked
      // This is handled by the computed property isAllDaysSelected
    },
    async submitCompleteForm() {
      // Validate form
      if (!this.completeForm.teacher_id) {
        this.errorMessage = 'يرجى اختيار المعلم';
        return;
      }

      if (!this.completeForm.region_id) {
        this.errorMessage = 'يرجى اختيار المنطقة';
        return;
      }

      if (!this.completeForm.prayer_name) {
        this.errorMessage = 'يرجى اختيار الصلاة';
        return;
      }

      if (this.completeForm.session_start_after_minutes < 0) {
        this.errorMessage = 'وقت بداية الجلسة الأولى يجب أن يكون 0 أو أكثر';
        return;
      }

      if (!this.completeForm.session_count || this.completeForm.session_count < 1) {
        this.errorMessage = 'عدد الجلسات يجب أن يكون أكبر من صفر';
        return;
      }

      if (!this.completeForm.session_duration_minutes || this.completeForm.session_duration_minutes < 1) {
        this.errorMessage = 'مدة الجلسة يجب أن تكون أكبر من صفر';
        return;
      }

      if (this.completeForm.schedule_type === 'custom') {
        if (!this.completeForm.start_date) {
          this.errorMessage = 'يرجى اختيار تاريخ بداية الجدول المخصص';
          return;
        }
        if (!this.completeForm.end_date) {
          this.errorMessage = 'يرجى اختيار تاريخ نهاية الجدول المخصص';
          return;
        }
        if (new Date(this.completeForm.end_date) < new Date(this.completeForm.start_date)) {
          this.errorMessage = 'تاريخ النهاية يجب أن يكون بعد أو يساوي تاريخ البداية';
          return;
        }
      }

      this.submitting = true;
      this.errorMessage = '';

      try {
        const formData = {
          region_id: this.completeForm.region_id,
          prayer_name: this.completeForm.prayer_name,
          schedule_type: this.completeForm.schedule_type,
          session_count: this.completeForm.session_count,
          session_duration_minutes: this.completeForm.session_duration_minutes,
          first_session_start_minutes_after_prayer: this.completeForm.session_start_after_minutes,
          session_start_after_minutes: this.completeForm.session_start_after_minutes,
          is_active: this.completeForm.is_active,
          priority: this.completeForm.priority,
        };

        // Add sessions_name if provided
        if (this.completeForm.sessions_name) {
          formData.sessions_name = this.completeForm.sessions_name;
        }

        if (this.completeForm.schedule_type === 'custom') {
          formData.start_date = this.completeForm.start_date;
          formData.end_date = this.completeForm.end_date;
          formData.archive_after_end = this.completeForm.archive_after_end;
        }

        // Handle selected days
        if (this.completeForm.selected_days && this.completeForm.selected_days.length > 0) {
          formData.selected_days = this.completeForm.selected_days;
        } else {
          formData.selected_days = []; // Empty array = all days
        }

        const result = await teacherScheduleService.createTeacherScheduleComplete(
          this.completeForm.teacher_id,
          formData
        );

        if (result && result.created > 0) {
          alert(`تم إنشاء ${result.created} جدول بنجاح`);
          this.closeModal();
          // Update selected teacher if not already selected
          if (!this.selectedTeacherId || this.selectedTeacherId !== this.completeForm.teacher_id) {
            this.selectedTeacherId = this.completeForm.teacher_id;
            await this.loadSchedules();
          } else {
            await this.loadSchedules();
          }
        } else {
          this.errorMessage = 'فشل في إنشاء الجدول';
        }
      } catch (error) {
        console.error('Error creating complete schedule:', error);
        this.errorMessage = error.message || 'فشل في إنشاء الجدول';
      } finally {
        this.submitting = false;
      }
    },
    closeModal() {
      this.showModal = false;
      this.editingSchedule = null;
      this.errorMessage = '';
      this.loadingPrayerTime = false;
    },
    formatTime(time) {
      if (!time) return 'N/A';
      
      let hours = 0;
      let minutes = 0;
      
      if (typeof time === 'string') {
        const parts = time.split(':');
        if (parts.length >= 2) {
          hours = parseInt(parts[0], 10);
          minutes = parseInt(parts[1], 10);
        } else {
          return time;
        }
      } else if (typeof time === 'number') {
        // If it's a timestamp or number
        const date = new Date(time);
        hours = date.getHours();
        minutes = date.getMinutes();
      } else {
        return time;
      }
      
      // Convert to 12-hour format
      const period = hours >= 12 ? 'م' : 'ص';
      const displayHours = hours % 12 || 12;
      const displayMinutes = minutes.toString().padStart(2, '0');
      
      return `${displayHours}:${displayMinutes} ${period}`;
    },
    parseTime(timeStr) {
      if (!timeStr) return null;
      const parts = timeStr.split(':');
      if (parts.length >= 2) {
        const date = new Date();
        date.setHours(parseInt(parts[0]), parseInt(parts[1]), 0);
        return date;
      }
      return null;
    },
    getPrayerNameArabic(prayerName) {
      if (!prayerName) return 'N/A';
      
      // Handle both English and Arabic formats
      const prayerNames = {
        // English formats (from API - uppercase)
        'FAJR': 'الفجر',
        'DHUHR': 'الظهر',
        'ASR': 'العصر',
        'MAGHRIB': 'المغرب',
        'ISHA': 'العشاء',
        // Lowercase formats
        'Fajr': 'الفجر',
        'Dhuhr': 'الظهر',
        'Asr': 'العصر',
        'Maghrib': 'المغرب',
        'Isha': 'العشاء',
        // Already in Arabic (if backend returns Arabic)
        'الفجر': 'الفجر',
        'الظهر': 'الظهر',
        'العصر': 'العصر',
        'المغرب': 'المغرب',
        'العشاء': 'العشاء',
      };
      
      // Try exact match first
      if (prayerNames[prayerName]) {
        return prayerNames[prayerName];
      }
      
      // Try case-insensitive match
      const upperCase = prayerName.toUpperCase();
      if (prayerNames[upperCase]) {
        return prayerNames[upperCase];
      }
      
      // Return original if no match found
      return prayerName;
    },
    getScheduleType(schedule) {
      const type = schedule?.scheduleType || schedule?.schedule_type;
      if (type) {
        return String(type).toLowerCase() === 'custom' ? 'custom' : 'regular';
      }
      const hasDateRange = Boolean(
        schedule?.startDate || schedule?.start_date || schedule?.endDate || schedule?.end_date
      );
      return hasDateRange ? 'custom' : 'regular';
    },
    getScheduleTypeLabel(schedule) {
      return this.getScheduleType(schedule) === 'custom' ? 'جدول مخصص' : 'جدول عادي';
    },
    getScheduleDateRange(schedule) {
      const start = schedule?.startDate || schedule?.start_date;
      const end = schedule?.endDate || schedule?.end_date;
      if (!start && !end) return 'غير محدد';
      const startText = start ? this.formatDate(start) : 'غير محدد';
      const endText = end ? this.formatDate(end) : 'غير محدد';
      return `${startText} - ${endText}`;
    },
    isScheduleExpired(schedule) {
      const end = schedule?.endDate || schedule?.end_date;
      if (!end) return false;
      const endDate = new Date(end);
      if (Number.isNaN(endDate.getTime())) return false;
      endDate.setHours(23, 59, 59, 999);
      return endDate.getTime() < Date.now();
    },
    formatDate(dateValue) {
      const date = new Date(dateValue);
      if (Number.isNaN(date.getTime())) return dateValue;
      return date.toLocaleDateString('ar-SA');
    },
    formatDateTime(dateValue) {
      const date = new Date(dateValue);
      if (Number.isNaN(date.getTime())) return dateValue;
      return date.toLocaleString('ar-SA');
    },
  },
});
</script>

<style scoped>
/* Add any custom styles here */
</style>

